import { CircleHelp } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import type { Placement } from '../../components/ui/layer';
import { Popover } from '../../components/ui/Popover';
import { cn } from '../../lib/cn';
import { getHint, type HintId } from './hints';
import { useTraining } from './TrainingProvider';

type HelpHintProps = {
  /** Ключ підказки зі словника hints.ts. */
  id: HintId | (string & {});
  className?: string;
  placement?: Placement;
  /** Підпис кнопки для зчитувачів; за замовчуванням — «Пояснення: <назва підказки>». */
  label?: string;
};

/**
 * Контекстна підказка «?» — лише в режимі навчання. Клік або фокус із клавіатури відкриває картку з поясненням;
 * Esc, клік поза нею чи втрата фокуса — закривають. Кліки й клавіші не доходять до батьківського рядка чи картки.
 */
export function HelpHint({ id, className, placement = 'bottom-start', label }: HelpHintProps) {
  const { enabled } = useTraining();
  const hint = getHint(id);
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popId = useId();

  if (!enabled) return null;
  if (!hint) {
    if (import.meta.env.DEV) console.warn(`[навчання] Невідома підказка «${id}»`);
    return null;
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label ?? `Пояснення: ${hint.title}`}
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-describedby={open ? popId : undefined}
        data-keep-panel=""
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        }}
        onFocus={(e) => {
          if (e.currentTarget.matches(':focus-visible')) setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-xs align-middle text-info-fg transition-colors hover:bg-info-soft',
          open && 'bg-info-soft',
          className,
        )}
      >
        <CircleHelp className="size-3.5" aria-hidden />
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={btnRef.current}
        placement={placement}
        role="tooltip"
        id={popId}
        initialFocus="none"
        restoreFocus={false}
        className="max-w-[min(320px,calc(100vw-16px))] p-3"
      >
        <p className="text-meta font-semibold text-fg">{hint.title}</p>
        <p className="mt-1 text-meta text-fg-2">{hint.text}</p>
      </Popover>
    </>
  );
}
